"use client";

import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useMemo, useRef, useState } from "react";

dayjs.extend(relativeTime);

const MIN_DATE_UPDATE_INTERVAL = 3 * 1000; // 3sec
const MAX_DATE_UPDATE_INTERVAL = 60 * 1000; // 1min

const HIDE_FULL_DATE_AFTER = 1 * 1000; // 1sec

export default function DateDisplay({
  date,
  dateShortToLocaleOptions,
  showDateFull = true,
  clipDue = false,
  emptyText = "Never",
  className = "",
}: {
  date: Date | string | number | undefined;
  dateShortToLocaleOptions?: Intl.DateTimeFormatOptions;
  showDateFull?: boolean;
  clipDue?: boolean;
  emptyText?: string;
  className?: string;
}) {
  const browserLocale = typeof window !== "undefined" && navigator?.languages[0] ? navigator.languages[0] : "en-US";
  const refTimerFullDateShown = useRef<NodeJS.Timeout>(null);
  const refTimerDateCurrentUpdate = useRef<NodeJS.Timeout>(null);

  const dateTarget = useMemo(() => new Date(date || 0), [date]);
  const [dateCurrent, setDateCurrent] = useState(new Date());
  const [fullDateShown, setFullDateShown] = useState(false);

  const dateShort = !date
    ? emptyText
    : dateTarget < dateCurrent && clipDue
    ? "Due"
    : dateShortToLocaleOptions
    ? dateTarget.toLocaleDateString("en-US", dateShortToLocaleOptions)
    : dayjs(dateTarget).from(dateCurrent);

  const dateFull = !date
    ? emptyText
    : dateTarget
    ? dateTarget.toLocaleDateString(browserLocale) + " " + dateTarget.toLocaleTimeString(browserLocale)
    : emptyText;

  useEffect(() => {
    // Initialize dateCurrent whenever date changes
    setDateCurrent(new Date());

    // Clear Timers on unmount
    return () => {
      /* eslint-disable react-hooks/exhaustive-deps */
      clearTimeout(refTimerFullDateShown.current as any);
      clearTimeout(refTimerDateCurrentUpdate.current as any);
      /* eslint-enable react-hooks/exhaustive-deps */
    };
  }, [date]);

  useEffect(() => {
    let timerDelay = Math.abs(dateTarget.getTime() - dateCurrent.getTime()) / 5.0;
    timerDelay = Math.max(timerDelay, MIN_DATE_UPDATE_INTERVAL);
    timerDelay = Math.min(timerDelay, MAX_DATE_UPDATE_INTERVAL);

    clearTimeout(refTimerDateCurrentUpdate.current as any);
    (refTimerDateCurrentUpdate.current as any) = setTimeout(() => {
      setDateCurrent(new Date());
    }, timerDelay);
  }, [dateTarget, dateCurrent]);

  return (
    <div className={classNames("overflow-visible", className)}>
      <time
        dateTime={dateTarget.toISOString()}
        onMouseEnter={() => {
          setFullDateShown(true);
        }}
        onMouseLeave={() => {
          clearTimeout(refTimerFullDateShown.current as any);
          (refTimerFullDateShown.current as any) = setTimeout(() => {
            setFullDateShown(false);
          }, HIDE_FULL_DATE_AFTER);
        }}
        suppressHydrationWarning
      >
        {showDateFull && fullDateShown ? dateFull : dateShort}
      </time>
    </div>
  );
}
